'use client'

import { CrunchesTable } from '@/app/(dashboard)/CrunchesTable'
import { FoldersList } from '@/app/(dashboard)/folders/FoldersList'
import { ProjectsList } from '@/app/(dashboard)/projects/ProjectsList'
import { useDashboardPermissions } from '@/app/core/hooks/dashboard/useDashboardPermissions'
import {
  SELECTED_TEAM_ID_KEY,
  SELECTED_TEAM_NAME_KEY,
} from '@dc-shared-web/lib/constants'
import { getCurrentTeam, Spinner, useAuth } from '@/dc-shared-web/src'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect } from 'react'
import { useDashboardListState } from '@/app/core/hooks/dashboard/useDashboardListState'
import { useDocumentCount } from '@/app/(dashboard)/dash/hooks/useDocumentCount'
import { DashboardHeader } from '@/app/(dashboard)/DashboardHeader'
import useCreateQueryString from '@/app/core/hooks/createQueryString'
import { useDocumentTitle } from '@uidotdev/usehooks'

const PageContent = () => {
  const searchParams = useSearchParams()
  const selectedTeamId = searchParams.get(SELECTED_TEAM_ID_KEY) || ''

  const {
    canViewExplorers,
    canViewFoldersOnDashboard,
    shouldShowActions,
    canViewCrunches,
  } = useDashboardPermissions('dashboard')

  const documentCount = useDocumentCount('dashboard')

  const { isLoading, foldersCount, projectsCount } = useDashboardListState(
    selectedTeamId,
    canViewExplorers,
    'dashboard'
  )

  const teamHasNoFoldersNorProjects = foldersCount > 0 || projectsCount > 0

  if (isLoading) {
    return (
      <div className='tw-flex tw-justify-center tw-items-center tw-h-full'>
        <Spinner />
      </div>
    )
  }

  return (
    <div className='tw-flex tw-flex-col tw-gap-6 tw-h-full'>
      <DashboardHeader
        selectedTeamId={selectedTeamId}
        documentCount={documentCount}
        currentTable='dashboard'
      >
        Dashboard
      </DashboardHeader>
      {canViewExplorers ? (
        <>
          {canViewFoldersOnDashboard ? (
            <FoldersList
              shouldShowActions={shouldShowActions}
              teamUuid={selectedTeamId}
              page='dashboard'
            />
          ) : null}
          <ProjectsList
            shouldShowActions={shouldShowActions}
            teamId={selectedTeamId}
          />
        </>
      ) : null}
      {canViewCrunches && (
        <CrunchesTable
          selectedTeamId={selectedTeamId}
          page='dashboard'
          currentTable='dashboard'
          canMoveCrunches={teamHasNoFoldersNorProjects}
        />
      )}
    </div>
  )
}

export default function Page() {
  useDocumentTitle('Dashboard | Document Crunch')

  const { user } = useAuth()
  const router = useRouter()
  const searchParams = useSearchParams()

  const selectedTeamId = searchParams.get(SELECTED_TEAM_ID_KEY) || ''

  const isUnaffiliatedUser = user._isUnaffiliatedUser
  const isSuperAdmin = user.is_super_admin
  const hasAccessToSelectedTeam =
    isSuperAdmin || user.teams.some((team) => team.id === selectedTeamId)
  // if the user does not have access to the team in  the teamid params,
  // the current team gets updated in the user object.
  const newTeamToSelect = getCurrentTeam(user)
  const { createQueryString } = useCreateQueryString()

  const queryStringWithNewTeamId = createQueryString({
    [SELECTED_TEAM_ID_KEY]: newTeamToSelect?.id,
    [SELECTED_TEAM_NAME_KEY]: newTeamToSelect?.name,
  })
  const dashRouteWithUpdatedTeamParams = `/dash?${queryStringWithNewTeamId}`

  // If user is unaffiliated, redirect to shared page
  // because they do not have access to the main dashboard tab
  // (default landing page for affiliated users)
  useEffect(() => {
    if (isUnaffiliatedUser) {
      router.push('/shared')
    }
  }, [isUnaffiliatedUser, router])

  // If user does not have access to the selected team, redirect to a team
  // that they do have access to.
  useEffect(() => {
    if (!hasAccessToSelectedTeam && newTeamToSelect?.id) {
      router.push(dashRouteWithUpdatedTeamParams)
    }
  }, [
    hasAccessToSelectedTeam,
    newTeamToSelect?.id,
    router,
    dashRouteWithUpdatedTeamParams,
  ])

  return <PageContent />
}
